// Storefront pricing engine (formatMoney, discount resolution). Extracted
// from theme.liquid so it caches; the page-specific inputs arrive via the
// tiny inline window.SBStorefrontPricingConfig object rendered in the head.
// Loaded synchronously in the head because parse-time pricing consumers need
// the resolved engine before their markup is painted.
(() => {
  const existingStorefrontPricing = window.SBStorefrontPricing;
  if (
    existingStorefrontPricing
    && typeof existingStorefrontPricing.formatMoney === 'function'
    && typeof existingStorefrontPricing.getDiscountRate === 'function'
    && typeof existingStorefrontPricing.parseCents === 'function'
    && typeof existingStorefrontPricing.resolvePrice === 'function'
  ) {
    return;
  }

  const parseCents = (value) => {
    const nextValue = Number.parseInt(`${value ?? ''}`.trim(), 10);
    return Number.isFinite(nextValue) ? nextValue : null;
  };

  const clampDiscountPercent = (value) => {
    if (!Number.isFinite(value)) return 0;
    return Math.min(100, Math.max(0, value));
  };

  const pricingConfig = window.SBStorefrontPricingConfig || {};
  const storefrontDiscountSnapshot = pricingConfig.discountSnapshot || null;
  const seasonalDiscountPercent = clampDiscountPercent(
    Number(storefrontDiscountSnapshot?.sitewidePercentage || 0)
  );
  const affiliateState = window.SBAffiliateState ? window.SBAffiliateState.read() : window.SBAffiliateAnnouncementState;
  const affiliateDiscountPercent = clampDiscountPercent(
    Number(affiliateState?.discountPercentage ?? storefrontDiscountSnapshot?.affiliatePercentage ?? pricingConfig.affiliateFallbackPercentage ?? 0)
  );
  let cartDiscountPercent = clampDiscountPercent(
    Number(pricingConfig.cartDiscountPercentage ?? 0)
  );
  const pageDiscountPercent = clampDiscountPercent(
    Number(pricingConfig.pageDiscountPercentage ?? 0)
  );
  // Shared cookie parser from theme.liquid's head script; falls back to the
  // prefetched mirror in case an older cached page shipped without it.
  const hasAffiliateSaleCookie = Boolean(affiliateState);
  const getEffectiveGlobalDiscountPercent = () => {
    if (cartDiscountPercent > 0 || pageDiscountPercent > 0) {
      return Math.max(cartDiscountPercent, pageDiscountPercent);
    }
    if (hasAffiliateSaleCookie) return affiliateDiscountPercent;
    return seasonalDiscountPercent;
  };

  const productOverridePercentById = new Map();
  Object.entries(storefrontDiscountSnapshot?.productPercentagesById || {}).forEach(([productId, discountPercent]) => {
    const normalizedProductId = `${productId || ''}`.trim();
    const percent = clampDiscountPercent(Number(discountPercent || 0));
    if (!normalizedProductId || normalizedProductId === '0' || percent <= 0) return;
    productOverridePercentById.set(normalizedProductId, percent);
  });

  const quantityOverrideTiersByProductId = new Map();
  Object.entries(storefrontDiscountSnapshot?.quantityPercentagesByProductId || {}).forEach(([productId, tiers]) => {
    const normalizedProductId = `${productId || ''}`.trim();
    if (!normalizedProductId || normalizedProductId === '0' || !Array.isArray(tiers)) return;
    const normalizedTiers = tiers
      .map((tier) => ({
        minimumQuantity: Math.max(1, Number.parseInt(`${tier?.minimumQuantity ?? ''}`, 10) || 1),
        percentage: clampDiscountPercent(Number(tier?.percentage || 0)),
      }))
      .filter((tier) => tier.percentage > 0)
      .sort((a, b) => a.minimumQuantity - b.minimumQuantity);
    if (normalizedTiers.length) quantityOverrideTiersByProductId.set(normalizedProductId, normalizedTiers);
  });

  const getDiscountPercentages = (productId, quantity = 1) => {
    const normalizedProductId = `${productId || ''}`.trim();
    const flatProductPercent = normalizedProductId
      ? Number(productOverridePercentById.get(normalizedProductId) || 0)
      : 0;
    const normalizedQuantity = Math.max(1, Number.parseInt(`${quantity ?? ''}`, 10) || 1);
    const quantityProductPercent = normalizedProductId
      ? (quantityOverrideTiersByProductId.get(normalizedProductId) || []).reduce(
          (highest, tier) => normalizedQuantity >= tier.minimumQuantity ? Math.max(highest, tier.percentage) : highest,
          0
        )
      : 0;
    const productPercent = Math.max(flatProductPercent, quantityProductPercent);

    return [getEffectiveGlobalDiscountPercent(), productPercent];
  };

  const discountRateFromPercentages = ([globalPercent, productPercent]) => {
    if (globalPercent > 0 && productPercent > 0) {
      return 1 - ((1 - globalPercent / 100) * (1 - productPercent / 100));
    }
    return Math.max(globalPercent, productPercent) / 100;
  };
  const getDiscountRate = (productId, quantity = 1) => discountRateFromPercentages(getDiscountPercentages(productId, quantity));

  // Keep decimal percentages exact until the final cent is rounded. Converting
  // to a binary rate first can put an exact half-cent just below the boundary.
  const roundDiscountedCents = (cents, percentages) => {
    let numerator = BigInt(cents);
    let denominator = 1n;
    percentages.forEach((percent) => {
      const [coefficient, exponent = '0'] = `${percent}`.split('e');
      const fractionLength = (coefficient.split('.')[1] || '').length - Number(exponent);
      const scale = 10n ** BigInt(Math.max(0, fractionLength));
      const units = BigInt(coefficient.replace('.', '')) * (10n ** BigInt(Math.max(0, -fractionLength)));
      const percentDenominator = 100n * scale;
      numerator *= percentDenominator - units;
      denominator *= percentDenominator;
    });
    return Number((numerator + denominator / 2n) / denominator);
  };

  const setCartDiscountPercentage = (discountPercent, { source = 'unknown' } = {}) => {
    const nextPercent = clampDiscountPercent(Number(discountPercent || 0));
    if (Math.abs(nextPercent - cartDiscountPercent) < Number.EPSILON) return false;
    cartDiscountPercent = nextPercent;
    if (typeof document.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
      document.dispatchEvent(new CustomEvent('sb:storefront-pricing:changed', {
        detail: {
          cartDiscountPercentage: cartDiscountPercent,
          source,
        },
      }));
    }
    return true;
  };

  const fallbackCurrency = pricingConfig.fallbackCurrency || 'USD';
  // Symbols come from the shared snippet (sb-currency-symbol-map.liquid),
  // serialized into the pricing config so server and client can never drift.
  // If the config field is missing (stale cached HTML), formatMoney falls
  // back to a "CODE amount" prefix until the page cache rolls over.
  const compactCurrencySymbols = (() => {
    const symbolMap = new Map();
    const serialized = `${pricingConfig.currencySymbols || ''}`;
    serialized.split('|').forEach((entry) => {
      const separatorIndex = entry.indexOf('=');
      if (separatorIndex < 1) return;
      symbolMap.set(entry.slice(0, separatorIndex).trim().toUpperCase(), entry.slice(separatorIndex + 1));
    });
    return symbolMap;
  })();

  const resolveCurrency = (preferredCurrency = '') => {
    const normalizedPreferredCurrency = `${preferredCurrency || ''}`.trim().toUpperCase();
    if (normalizedPreferredCurrency) return normalizedPreferredCurrency;
    return `${fallbackCurrency || ''}`.trim().toUpperCase();
  };

  const normalizeContext = (context = 'storefront') => {
    const normalized = `${context || ''}`.trim().toLowerCase();
    return normalized === 'cart' ? 'cart' : 'storefront';
  };

  // Liquid formats this fixed sample in the active presentment currency. Store
  // money settings alone cannot describe Shopify's other currency formats.
  const moneySampleParts = `${pricingConfig.moneyFormatSample || '1,234,567.89'}`.match(/\d+|\D+/g);
  const moneyFractionDigits = moneySampleParts.at(-1).length === 2 ? 2 : 0;
  const moneyIntegerParts = moneyFractionDigits ? moneySampleParts.slice(0, -2) : moneySampleParts;
  const moneyGroups = moneyIntegerParts.filter((part) => /^\d+$/.test(part));
  const moneyGroupSeparator = moneyIntegerParts.find((part) => /^\D+$/.test(part)) || '';
  const moneyDecimalSeparator = moneyFractionDigits ? moneySampleParts.at(-2) : '.';
  const moneyAmountFormatter = new Intl.NumberFormat(
    moneyGroups.length > 2 && moneyGroups.at(-2).length === 2 ? 'en-IN' : 'en-US',
    {
      useGrouping: Boolean(moneyGroupSeparator),
      minimumFractionDigits: moneyFractionDigits,
      maximumFractionDigits: moneyFractionDigits,
    }
  );

  const formatMoney = (cents, currencyOverride = '', options = {}) => {
    const normalizedCents = Number.isFinite(Number(cents)) ? Math.round(Number(cents)) : 0;
    const context = normalizeContext(options?.context || 'storefront');
    const displayCents = context === 'cart'
      ? normalizedCents
      : Math.sign(normalizedCents) * Math.round(Math.abs(normalizedCents) / 100) * 100;
    const normalizedCurrencyCode = resolveCurrency(currencyOverride);
    const compactSymbol = compactCurrencySymbols.get(normalizedCurrencyCode) || (normalizedCurrencyCode ? `${normalizedCurrencyCode} ` : '');
    const displayAmount = displayCents / 100;
    const sign = displayAmount < 0 ? '-' : '';
    const parts = moneyAmountFormatter.formatToParts(Math.abs(displayAmount));
    const omitFraction = parts.some((part) => part.type === 'fraction' && /^0+$/.test(part.value));
    const amount = parts.map((part) => {
      if (part.type === 'group') return moneyGroupSeparator;
      if (part.type === 'decimal') return omitFraction ? '' : moneyDecimalSeparator;
      if (part.type === 'fraction' && omitFraction) return '';
      return part.value;
    }).join('');
    return `${sign}${compactSymbol}${amount}`;
  };

  const resolvePrice = ({
    productId = '',
    priceCents = null,
    comparePriceCents = null,
    quantity = 1,
  } = {}) => {
    const normalizedPriceCents = parseCents(priceCents);
    if (!Number.isFinite(normalizedPriceCents) || normalizedPriceCents <= 0) {
      return {
        currentPriceCents: null,
        comparePriceCents: null,
        hasComparePrice: false,
        discountRate: 0,
      };
    }

    const percentages = getDiscountPercentages(productId, quantity);
    const discountRate = discountRateFromPercentages(percentages);
    if (percentages.some((percent) => percent > 0)) {
      const discountedPriceCents = roundDiscountedCents(normalizedPriceCents, percentages);
      return {
        currentPriceCents: discountedPriceCents,
        comparePriceCents: normalizedPriceCents,
        hasComparePrice: normalizedPriceCents > discountedPriceCents,
        discountRate,
      };
    }

    const normalizedComparePriceCents = parseCents(comparePriceCents);
    if (Number.isFinite(normalizedComparePriceCents) && normalizedComparePriceCents > normalizedPriceCents) {
      return {
        currentPriceCents: normalizedPriceCents,
        comparePriceCents: normalizedComparePriceCents,
        hasComparePrice: true,
        discountRate: 0,
      };
    }

    return {
      currentPriceCents: normalizedPriceCents,
      comparePriceCents: null,
      hasComparePrice: false,
      discountRate: 0,
    };
  };

  window.SBStorefrontPricing = {
    formatMoney,
    getDiscountRate,
    parseCents,
    resolvePrice,
    setCartDiscountPercentage,
  };
})();
